import AccountPackages from "@/components/feature/panel/AccountPackages";
import ChildLearningProgress from '@/components/feature/panel/ChildLearningProgress';
import { ParentOverview } from "@/components/feature/mobile/Experience";
import { useEffect, useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import { useAuth } from "@/hooks/useAuth";
import { getDataClient } from "@/lib/dataClient";
import { resolvePanelUser } from "@/lib/profile";

interface ChildRow {
  id: string;
  full_name: string;
  grade: string | null;
  city: string | null;
}

const areas = [
  { icon: "ri-file-chart-line", title: "Haftalık özet", text: "Henüz haftalık özet yok." },
  { icon: "ri-vidicon-line", title: "Ders katılımı", text: "Henüz katılım verisi yok." },
  { icon: "ri-task-line", title: "Ödev durumu", text: "Henüz ödev kaydı yok." },
  { icon: "ri-file-text-line", title: "Deneme sonuçları", text: "Henüz deneme sonucu yok." },
  { icon: "ri-chat-smile-2-line", title: "Öğretmen notları", text: "Henüz öğretmen notu yok." },
];

const steps = [
  {
    id: 1,
    title: "Öğrenci hesabını bağla",
    detail: "Öğrencinin hesabı veli hesabına bağlandığında gelişimi tek yerden izlenir.",
  },
  {
    id: 2,
    title: "Bildirim tercihlerini ayarla",
    detail: "Ders, ödev ve deneme sonuçları için hangi bildirimleri alacağını seç.",
  },
  {
    id: 3,
    title: "Gelişim raporlarını takip et",
    detail: "Haftalık raporlar ve analizlerle öğrencinin ilerlemesini düzenli izle.",
  },
];

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toLocaleUpperCase("tr-TR");
  return (parts[0][0] + parts[parts.length - 1][0]).toLocaleUpperCase("tr-TR");
}

export default function VeliDashboard() {
  const { user } = useAuth();
  const identity = resolvePanelUser(user);
  const firstName = identity.name ? identity.name.split(" ")[0] : "";

  const [children, setChildren] = useState<ChildRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadChildren = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const dataClient = getDataClient();
      const { data, error: err } = await dataClient
        .from("profiles")
        .select("id, full_name, grade, city")
        .eq("parent_id", user.id)
        .eq("role", "ogrenci");
      if (err) {
        setError("Öğrenci bilgileri yüklenemedi.");
      } else {
        setChildren((data ?? []) as ChildRow[]);
      }
    } catch {
      setError("Beklenmeyen bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <div className="space-y-6">
      <AccountPackages />
      {children.map(child=><ChildLearningProgress key={child.id} id={child.id} name={child.full_name} />)}
      <ParentOverview name={firstName || "Veli"} children={children} loading={loading} />
      {error && <div role="alert" className="rounded-xl bg-red-50 p-4 text-sm text-red-800">{error}<button className="ml-3 underline" onClick={loadChildren}>Tekrar dene</button></div>}

      {/* Ana çalışma alanları */}
      <section aria-label="Çalışma alanları">
        <h2 className="font-heading font-semibold text-base text-foreground-950">Çalışma alanları</h2>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {areas.map((a) => (
            <div
              key={a.title}
              className="rounded-lg border border-background-200 bg-background-50 p-4 flex items-start gap-3"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 shrink-0">
                <i className={`${a.icon} text-base`} aria-hidden="true"></i>
              </span>
              <div className="min-w-0">
                <p className="font-heading font-semibold text-sm text-foreground-900">{a.title}</p>
                <p className="mt-0.5 text-xs text-foreground-500">{a.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sıradaki adımlar */}
      <section aria-label="Veli için sıradaki adımlar">
        <h2 className="font-heading font-semibold text-base text-foreground-950">Veli için sıradaki adımlar</h2>
        <ol className="mt-3 space-y-3">
          {steps.map((s) => (
            <li key={s.id} className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 text-accent-800 font-heading font-semibold text-sm shrink-0">
                {s.id}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-900">{s.title}</p>
                <p className="mt-0.5 text-xs text-foreground-500">{s.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
